import React, { useEffect, useMemo, useState } from "react";
import ApiService from "../../../Services/apiService";

const MONTHS_RU = [
	"января",
	"февраля",
	"марта",
	"апреля",
	"мая",
	"июня",
	"июля",
	"августа",
	"сентября",
	"октября",
	"ноября",
	"декабря",
];

function TemplateDocumentView({ templateId, templateContent }) {
	const [body, setBody] = useState("");
	const templateData = useMemo(() => {
		if (!templateContent) return null;
		try {
			return JSON.parse(templateContent);
		} catch {
			return null;
		}
	}, [templateContent]);

	useEffect(() => {
		if (!templateId) return;
		load(templateId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [templateId, templateContent]);

	const load = async id => {
		const tplRes = await ApiService.getDocumentTemplateById(id);
		const templateBody = tplRes?.data?.body || "";
		if (!templateBody) {
			setBody("");
			return;
		}
		const infoRes = await ApiService.getDocumentTemplateInfoInTemplateId(id);
		const info = infoRes?.data || [];
		const rendered = setTemplateBody(info, templateBody);
		setBody(rendered);
	};

	const setDate = dateStr => {
		if (!dateStr) return "";
		const date = new Date(dateStr);
		const monthName = MONTHS_RU[date.getMonth()] || "";
		return `${date.getDate()} ${monthName} ${date.getFullYear()}`;
	};

	const setTemplateBody = (templateInfo, templateBody) => {
		if (!templateInfo?.length || !templateBody) return templateBody || "";
		if (!templateData) return templateBody;

		let bodyStr = templateBody;
		let tableRowCount = 1;
		const tableTds = [];

		const placeholderRe = pos => new RegExp(`\\{[aA]${pos}\\}`, "g");
		const pPlaceholderRe = pos => new RegExp(`<p>\\{[aA]${pos}\\}<\\/p>`, "g");

		const getCommissionNamesForSignBlock = signPosition => {
			const direct = templateData[`a${signPosition}`];
			if (Array.isArray(direct) && direct.some(x => x != null && String(x).trim())) {
				return direct.map(x => String(x ?? "")).filter(s => s.trim());
			}
			const prevInputs = (templateInfo || []).find(
				x => x.type === "inputs-list" && Number(x.position) === Number(signPosition) - 1,
			);
			if (prevInputs) {
				const pv = templateData[`a${prevInputs.position}`];
				if (Array.isArray(pv) && pv.some(x => x != null && String(x).trim())) {
					return pv.map(x => String(x ?? "")).filter(s => s.trim());
				}
			}
			const onlyInputs = (templateInfo || []).filter(x => x.type === "inputs-list");
			if (onlyInputs.length === 1) {
				const pv = templateData[`a${onlyInputs[0].position}`];
				if (Array.isArray(pv) && pv.some(x => x != null && String(x).trim())) {
					return pv.map(x => String(x ?? "")).filter(s => s.trim());
				}
			}
			return [];
		};

		templateInfo.forEach(info => {
			let dataValue = templateData[`a${info.position}`];
			if (dataValue === "Другое (вписать вручную)") dataValue = "";

			const pos = info.position;
			const hasPlaceholder = placeholderRe(pos).test(bodyStr);

			if (info.type === "organization-name" && hasPlaceholder) {
				bodyStr = bodyStr.replace(
					pPlaceholderRe(pos),
					`<div class="center"><span class="organization-name-title">${dataValue || ""}</span></div>`,
				);
			} else if (info.type === "input-number" && hasPlaceholder) {
				bodyStr = bodyStr.replace(
					new RegExp(`<p>№ \\{[aA]${pos}\\}<\\/p>`, "g"),
					`<div class="direction-right"><span class="template-data-field direction-right">№ ${dataValue || ""}</span></div>`,
				);
				bodyStr = bodyStr.replace(placeholderRe(pos), `${dataValue || ""}`);
			} else if (
				(info.type === "input-create-date" || info.type === "input-date") &&
				hasPlaceholder
			) {
				if (dataValue) {
					const formatted = setDate(dataValue);
					if (info.type === "input-create-date") {
						bodyStr = bodyStr.replace(
							pPlaceholderRe(pos),
							`<span class="template-data-field">${formatted}</span>`,
						);
					} else {
						bodyStr = bodyStr.replace(placeholderRe(pos), `${formatted}`);
					}
				} else {
					bodyStr = bodyStr.replace(pPlaceholderRe(pos), ``);
					bodyStr = bodyStr.replace(placeholderRe(pos), ``);
				}
			} else if (
				info.type === "sign-row-arr" &&
				hasPlaceholder
			) {
				const usersList = getCommissionNamesForSignBlock(pos);
				if (usersList.length) {
					bodyStr = bodyStr.replace(
						placeholderRe(pos),
						`<div class="input-list"><div class="flexable sign-row-mrg"><span class="empty-row"></span><span class="template-data-field">${usersList[0]}</span></div></div>`,
					);
					for (let i = 1; i < usersList.length; i++) {
						const prevIndex = i - 1;
						bodyStr = bodyStr.replace(
							`${usersList[prevIndex]}</span></div></div>`,
							`${usersList[prevIndex]}</span></div><div class="flexable sign-row-mrg"><span class="empty-row"></span><span class="template-data-field">${usersList[i]}</span></div></div>`,
						);
					}
				} else {
					bodyStr = bodyStr.replace(placeholderRe(pos), ``);
				}
			} else if (info.type === "sign-row" && hasPlaceholder) {
				// Always render signature line; name is optional
				const name = dataValue ? String(dataValue) : "";
				const nameHtml = `<span class="template-data-field">${name}</span>`;
				// Use inline-flex + no-wrap to avoid wrapping inside `<p class="draw-flex-wrap">`
				const html = `<span class="sign-row-mrg" style="display:inline-flex;flex-wrap:nowrap;align-items:flex-end;"><span class="empty-row"></span>${nameHtml}</span>`;
				bodyStr = bodyStr.replace(pPlaceholderRe(pos), html);
				bodyStr = bodyStr.replace(placeholderRe(pos), html);
			} else if (typeof dataValue === "object" && hasPlaceholder) {
				const arr = dataValue;
				if (Array.isArray(arr) && arr.length) {
					bodyStr = bodyStr.replace(
						placeholderRe(pos),
						`<div class="input-list"><span class="template-data-field">1. ${arr[0]}</span></div>`,
					);
					for (let i = 1; i < arr.length; i++) {
						const nextIndex = i + 1;
						const prevIndex = i - 1;
						bodyStr = bodyStr.replace(
							`${arr[prevIndex]}</span></div>`,
							`${arr[prevIndex]}</span><span class="template-data-field">${nextIndex}. ${arr[i]}</span></div>`,
						);
					}
				} else {
					bodyStr = bodyStr.replace(placeholderRe(pos), ``);
				}
			} else if (
				info.type === "table-input-string" ||
				info.type === "table-input-date" ||
				info.type === "table-input-number" ||
				info.type === "table-library" ||
				info.type === "table-list" ||
				info.type === "table-sign-part" ||
				info.type === "table-number-and-date" ||
				info.type === "table-input-start-end-time"
			) {
				tableTds.push(`<td>{a${info.position}}</td>`);
				const tableData = templateData["tableData"];
				if (Array.isArray(tableData) && tableData.length) {
					bodyStr = bodyStr.replace(
						`<td>{a${info.position}}</td>`,
						`<td class="col${info.position}-${tableData[0].number}">{a${info.position}}</td>`,
					);
					bodyStr = bodyStr.replace(
						`<td>№ {a${info.position}}</td>`,
						`<td class="col${info.position}-${tableData[0].number}">№ {a${info.position}}</td>`,
					);
				} else {
					bodyStr = bodyStr.replace(`<td>№ {a${info.position}}</td>`, `<td></td>`);
					bodyStr = bodyStr.replace(`<td>{a${info.position}}</td>`, `<td></td>`);
				}
			} else if (!templateData[`a${info.position}`] && info.type !== "sign-row") {
				// For protocols "commission" might be stored only in inputs-list (e.g. a22), so don't clear sign-row-arr placeholder too early.
				if (info.type === "sign-row-arr") {
					const usersList = getCommissionNamesForSignBlock(pos);
					if (!usersList.length) bodyStr = bodyStr.replace(placeholderRe(pos), "");
				} else {
					bodyStr = bodyStr.replace(placeholderRe(pos), "");
				}
			} else if (info.type === "input-date-to-right" && hasPlaceholder) {
				bodyStr = bodyStr.replace(pPlaceholderRe(pos), `<div class="direction-right">${dataValue || ""}</div>`);
			} else if (info.type === "input-short-string" && hasPlaceholder) {
				bodyStr = bodyStr.replace(pPlaceholderRe(pos), `${dataValue || ""}`);
			} else if (info.type === "drop-down-group-not-draw" && hasPlaceholder) {
				bodyStr = bodyStr.replace(pPlaceholderRe(pos), ``);
			} else {
				bodyStr = bodyStr.replace(placeholderRe(pos), `${dataValue || ""}`);
			}

			if (!dataValue) {
				bodyStr = bodyStr.replace(pPlaceholderRe(pos), ``);
			}
		});

		// table values fill (basic)
		const tableData = templateData["tableData"];
		if (Array.isArray(tableData) && tableData.length) {
			bodyStr = bodyStr.replace("<tr>", `<tr class="row-th">`);
			bodyStr = bodyStr.replace("<tr>", `<tr class="row-${tableData[0].number}">`);

			tableData.forEach(item => {
				if (!bodyStr.includes(`<tr class="row-${item.number}">`)) {
					let row = `<tr class="row-${item.number}"><td>${++tableRowCount}</td></tr>`;
					tableTds.forEach(tdItem => {
						const start = tdItem.indexOf("{a") + 2;
						const end = tdItem.indexOf("}");
						const col = tdItem.substring(start, end);
						const withClass = tdItem.replace("<td>", `<td class="col${col}-${item.number}">`);
						row = row.replace("</tr>", `${withClass}</tr>`);
					});
					const lastIndexTR = bodyStr.lastIndexOf("</tr>");
					const lastIndexTBody = bodyStr.lastIndexOf("</tbody>") + 8;
					const subStr = bodyStr.substring(lastIndexTR, lastIndexTBody);
					bodyStr = bodyStr.replace(subStr, `</tr>${row}</tbody>`);
				}

				const info = templateInfo.find(i => String(i.position) === String(item.columnNumber));
				if (!info) return;

				if (typeof item.value === "object" && info.type === "table-number-and-date") {
					const number = item.value?.[0];
					const date = item.value?.[1];
					let text = "";
					if (number) text += `№ ${number}`;
					if (date) text += `${text ? " " : ""}от ${setDate(date)}`;
					bodyStr = bodyStr.replace(
						`<td class="col${item.columnNumber}-${item.number}">{a${item.columnNumber}}</td>`,
						`<td class="col${item.columnNumber}-${item.number}">${text}</td>`,
					);
				} else if (info.type === "table-input-date") {
					bodyStr = bodyStr.replace(
						`<td class="col${item.columnNumber}-${item.number}">{a${item.columnNumber}}</td>`,
						`<td class="col${item.columnNumber}-${item.number}">${setDate(item.value)}</td>`,
					);
				} else if (item.timeType === "start") {
					const endValue = tableData.find(
						t =>
							String(t.columnNumber) === String(item.columnNumber) &&
							String(t.number) === String(item.number) &&
							t.timeType === "end",
					);
					const text = `${item.value || ""} - ${endValue?.value || ""}`;
					bodyStr = bodyStr.replace(
						`<td class="col${item.columnNumber}-${item.number}">{a${item.columnNumber}}</td>`,
						`<td class="col${item.columnNumber}-${item.number}">${text}</td>`,
					);
				} else if (!item.timeType) {
					bodyStr = bodyStr.replace(
						`<td class="col${item.columnNumber}-${item.number}">{a${item.columnNumber}}</td>`,
						`<td class="col${item.columnNumber}-${item.number}">${item.value ?? ""}</td>`,
					);
					bodyStr = bodyStr.replace(
						`<td class="col${item.columnNumber}-${item.number}">№ {a${item.columnNumber}}</td>`,
						`<td class="col${item.columnNumber}-${item.number}">№ ${item.value ?? ""}</td>`,
					);
				}
			});

			templateInfo.forEach(info => {
				const lastN = Number(tableData[tableData.length - 1].number) + 1;
				for (let i = 0; i < lastN; i++) {
					bodyStr = bodyStr.replace(
						`<td class="col${info.position}-${i}">{a${info.position}}</td>`,
						`<td class="col${info.position}-${i}"></td>`,
					);
					bodyStr = bodyStr.replace(
						`<td class="col${info.position}-${i}">№ {a${info.position}}</td>`,
						`<td class="col${info.position}-${i}"></td>`,
					);
				}
			});
		} else {
			bodyStr = bodyStr.replace("<tr>", `<tr class="row-th">`);
		}

		bodyStr = bodyStr.replace("<td></td>", "");
		bodyStr = bodyStr.replace("<td>Действия</td>", "");
		bodyStr = bodyStr.replace("<table", '<div class="draw-template-table-container"><table class="draw-template-table"');
		bodyStr = bodyStr.replace("</table>", "</table></div>");
		bodyStr = bodyStr.replace("<h1>", '<h1 class="center template-title">');
		bodyStr = bodyStr.replace("<h2>", '<h2 class="center">');
		bodyStr = bodyStr.replace("<h3>", '<h3 class="center template-title margin-h3">');
		bodyStr = bodyStr.replace("<h3>", '<h3 class="doc-sub-title">');
		bodyStr = bodyStr.split("<p>").join('<p class="draw-flex-wrap" style="gap: 10px;">');
		return bodyStr;
	};

	return body ? (
		<div dangerouslySetInnerHTML={{ __html: body }} />
	) : null;
}

export default TemplateDocumentView;

